import {
  tablesDB,
  DATABASE_ID,
  ADMIN_ACTIVITY_LOG_TABLE_ID,
  ID,
  Query,
} from '../lib/appwrite';
import AdminAuthService from './adminAuthService';

/**
 * Log an admin activity. Call this after successful mutations.
 * @param {Object} params
 * @param {string} params.action - create | update | delete | view | login
 * @param {string} params.resourceType - store | product | order | user | admin_user | cart | etc.
 * @param {string} [params.resourceId] - ID of the affected resource
 * @param {string} [params.details] - Human-readable description
 */
export async function logActivity({ action, resourceType, resourceId, details }) {
  try {
    const [user, userRow] = await Promise.all([
      AdminAuthService.getCurrentUser().catch(() => null),
      AdminAuthService.getUserRow().catch(() => null),
    ]);
    const adminUserId = user?.$id || 'unknown';
    const adminUserEmail = user?.email || '';
    const adminUserName = [userRow?.fname, userRow?.lname].filter(Boolean).join(' ') || adminUserEmail || adminUserId;

    const data = {
      adminUserId: adminUserId.substring(0, 64),
      adminUserEmail: (adminUserEmail || '').substring(0, 200),
      adminUserName: (adminUserName || '').substring(0, 200),
      action: (action || 'view').substring(0, 50),
      resourceType: (resourceType || 'unknown').substring(0, 100),
      resourceId: (resourceId || '').substring(0, 200),
      details: (details || '').substring(0, 2000),
      createdAt: new Date().toISOString(),
    };
    await tablesDB.createRow(
      DATABASE_ID,
      ADMIN_ACTIVITY_LOG_TABLE_ID,
      ID.unique(),
      data,
      null,
      true
    );
  } catch (err) {
    console.error('[adminActivity] Failed to log activity:', err.message, err);
  }
}

/**
 * Get activity log with filters
 * @param {Object} options - { adminUserId, action, resourceType, timeRange, limit }
 */
export async function getActivityLog(options = {}) {
  const { adminUserId, action, resourceType, timeRange = 'month', limit = 200 } = options;

  const queries = [
    Query.orderDesc('createdAt'),
    Query.limit(limit),
  ];
  if (adminUserId) queries.push(Query.equal('adminUserId', adminUserId));
  if (action) queries.push(Query.equal('action', action));
  if (resourceType) queries.push(Query.equal('resourceType', resourceType));

  const res = await tablesDB.listRows(DATABASE_ID, ADMIN_ACTIVITY_LOG_TABLE_ID, queries);
  let rows = res.rows || res.documents || [];

  if (timeRange && timeRange !== 'all') {
    const { filterByTimeRange } = await import('../utils/timeRange');
    rows = filterByTimeRange(rows, timeRange);
  }

  return rows;
}

/**
 * Get unique admin users and filter options from activity
 */
export async function getActivityFilterOptions() {
  const res = await tablesDB.listRows(DATABASE_ID, ADMIN_ACTIVITY_LOG_TABLE_ID, [
    Query.limit(2000),
    Query.orderDesc('createdAt'),
  ]);
  const rows = res.rows || res.documents || [];
  const adminIds = new Map();
  const actions = new Set();
  const resourceTypes = new Set();
  rows.forEach((r) => {
    if (r.adminUserId) {
      adminIds.set(r.adminUserId, {
        id: r.adminUserId,
        name: r.adminUserName || r.adminUserEmail || r.adminUserId,
        email: r.adminUserEmail,
      });
    }
    if (r.action) actions.add(r.action);
    if (r.resourceType) resourceTypes.add(r.resourceType);
  });
  return {
    admins: Array.from(adminIds.values()),
    actions: Array.from(actions).sort(),
    resourceTypes: Array.from(resourceTypes).sort(),
  };
}
